import React, { useState, useEffect } from 'react';
import { flowRight, get, isNull, isEmpty } from 'lodash';
import { connect, useDispatch } from 'react-redux';
import { setItem } from 'api/actions';
import PropTypes from 'prop-types';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import GenericListPair from 'modules/common/components/GenericListPair';
import useDebounce from 'modules/common/components/UseDebounce';
import Content from './Content';
import * as actions from '../actions';
import * as c from '../constant';

const initialFormData = {
  transaction_status: '',
  keyword: '',
};

const statusOptions = [
  { label: 'ALL STATUS', value: '' },
  { label: 'FOR PERMIT PROCESS', value: 'FOR PERMIT PROCESS' },
  { label: 'FOR LICENSE ASSESSMENT', value: 'FOR LICENSE ASSESSMENT' },
  { label: 'FOR RESUBMISSION', value: 'FOR RESUBMISSION' },
  { label: 'PENDING', value: 'PENDING' },
  { label: 'WAITING FOR PAYMENT', value: 'WAITING FOR PAYMENT' },
  { label: 'EXPIRED REFERENCE NUMBER', value: 'EXPIRED REFERENCE NUMBER' },
  { label: 'PAID', value: 'PAID' },
  { label: 'FOR DELIVERY', value: 'FOR DELIVERY' },
  { label: 'RECEIVED', value: 'RECEIVED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

function Applications({
  getList,
  list,
  getItem,
  match,
  pager,
  isLoading,
  history,
  clearList,
  clearItem,
}) {
  const [formData, setFormData] = useState(initialFormData);
  const [refreshList, setRefreshList] = useState('');
  const [filter, setFilter] = useState(initialFormData);
  const [countChar, setCountChar] = useState(0);

  const dispatch = useDispatch();

  const getType = () => {
    if (match.params.transaction_type.toUpperCase() === 'RENEWAL') {
      return 'RENEWAL';
    }
    if (match.params.transaction_type.toUpperCase() === 'CHANGE') {
      return 'CHANGE';
    }
    if (match.params.transaction_type.toUpperCase() === 'QUARTERLY') {
      return 'QUARTERLY';
    }
    if (match.params.transaction_type.toUpperCase() === 'DELINQUENT') {
      return 'DELINQUENT';
    }
    if (match.params.transaction_type.toUpperCase() === 'RETIREMENT') {
      return 'RETIREMENT';
    }
    return 'NEW';
  };

  const param = {
    module_type: 'BPLS',
    q: formData.keyword,
    sort_by: 'asc',
    transaction_status: filter.transaction_status,
    transaction_type: getType(),
  };

  useEffect(() => {
    getList(param);
    
    return () => {
      clearList(c.LIST, []);
      clearItem(c.ITEM, {});
      clearList(c.INITIAL_COMPUTATION, []);
      clearList(c.CALCULATION, []);
      clearItem(c.SELECTED_CALCULATION, '');
    };
  }, [`${Object.values(filter).join(',')}`, getType()]);

  useEffect(() => {
    if (!refreshList) {
      getList(param);
      getItem(refreshList)
    }
    setRefreshList('')
  }, [refreshList])

  const onSetKeyWord = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    setCountChar(e.target.value.length);
  };

  const onSetStatusFilter = key => e => {
    setFilter({
      ...filter,
      [key]: e.value,
    });
    getList({
      ...param,
      [key]: e.value, // transaction_status
    });
  };

  const debouncedSearchTerm = useDebounce(formData, 500);

  useEffect(() => {
    if (debouncedSearchTerm && countChar >= 3) {
      setFilter({
        ...filter,
        keyword: formData.keyword,
        transaction_type: getType(),
      });
      getList(param);
    } else {
      setFilter({
        ...filter,
        transaction_type: getType(),
      });
      getList({
        module_type: 'BPLS',
        sort_by: 'asc',
        transaction_status: filter.transaction_status,
        transaction_type: getType(),
      });
    }
  }, [debouncedSearchTerm]);

  const filterStatusOptions = () => {
    // remove/filter out the following keyValues from statusOptions based on BPLS Application
    let keyValue = [];
    if (getType() === 'QUARTERLY') {
      keyValue = [
        'FOR PERMIT PROCESS',
        'FOR LICENSE ASSESSMENT',
        'FOR RESUBMISSION',
        'FOR DELIVERY',
        'RECEIVED',
      ];
    }
    if (getType() === 'DELINQUENT' || getType() === 'RETIREMENT') {
      keyValue = ['FOR PERMIT PROCESS', 'FOR DELIVERY', 'RECEIVED'];
    }
    // if (getType() === "NEW" || getType() === "RENEWAL" || getType() === "CHANGE") {
    //   keyValue = ['PAID']
    // }

    return statusOptions.filter(item => keyValue.indexOf(item.label) === -1);
  };

  useEffect(() => {
    // reset search field if user select different application tab (new, renew, change... etc)
    setFormData({
      ...formData,
      keyword: '',
    });

    // reset filter status to default
    setFilter(prevFilter => ({
      ...prevFilter,
      keyword: '',
      transaction_status: '',
    }));
  }, [getType()]);

  const onCallbackTimer = () => {
    getList({
      module_type: 'BPLS',
      q: formData.keyword,
      transaction_status: filter.transaction_status,
      transaction_type: getType(),
    });
  };

  useEffect(() => {
    dispatch(setItem(c.CALLBACK_TIMER, onCallbackTimer));
  }, [getType()]);

  const bplsNotification = item => {
    return (
      ['BPLS'].indexOf(get(item, 'attributes.module_type')) > -1 &&
      ['NEW', 'RENEWAL', 'DELINQUENT', 'CHANGE', 'RETIREMENT'].indexOf(
        get(item, 'attributes.transaction_type')
      ) > -1 &&
      ['FOR LICENSE ASSESSMENT', 'FOR PERMIT PROCESS'].indexOf(
        get(item, 'included.bplsApplication.attributes.status')
      ) > -1 &&
      (isNull(
        get(item, 'included.bplsApplication.attributes.transmittal_no')
      ) ||
        isEmpty(
          get(item, 'included.bplsApplication.attributes.transmittal_no')
        ))
    );
  };

  return (
    <div className="card-content-listing  pt-1 pl-0">
      <Route
        path={`${match.path}/:id?`}
        render={({ match: { params } }) => (
          <GenericListPair
            statusOptions={filterStatusOptions()}
            notificationListConfig={bplsNotification}
            moduleName="Applications"
            getData={getList}
            setParams={param}
            setKeyword={onSetKeyWord}
            setStatusFilter={onSetStatusFilter('transaction_status')}
            status={get(filter, 'transaction_status') || ''}
            keyword={get(formData, 'keyword') || ''}
            keywordPlaceholder="Search Transaction..."
            pathName={`${match.path}/:id?`}
            listName="Application"
            label="Application List"
            renderItem={item => (
              <div className="px-3 py-3">
                <div className="transaction-list-company">
                  {get(
                    item,
                    'included.bplsApplication.attributes.business_name'
                  )}
                </div>
                <h4 className="mt-1 mb-0 transaction-list-name">
                  {get(item, 'included.bplsApplication.attributes.bin') || ''}
                </h4>
              </div>
            )}
            isLoading={isLoading}
            statusFilter
            selected={params.id}
            data={list}
            dataPager={pager}
            handleSelectRow={data => {
              history.push(`${match.url}/${get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:bpls_id?`}
                    render={() => <Content list={list} refreshList={setRefreshList} />}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:bpls_id?`}
                  render={() =>
                    !params.bpls_id && list.length > 0 ? (
                      <Redirect to={`${match.url}/${get(list, '0.id')}`} />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

const mapStateToProps = ({ api }) => {
  return {
    list: get(api, `${c.LIST}.list`) || [],
    pager: get(api, `${c.LIST}.pager`) || {},
    isLoading: api.loading.indexOf(c.LIST) > -1,
  };
};

const enhance = flowRight([withRouter, connect(mapStateToProps, actions)]);

Applications.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
  pager: PropTypes.instanceOf(Object).isRequired,
  getList: PropTypes.instanceOf(Function).isRequired,
  list: PropTypes.instanceOf(Array).isRequired,
  getItem: PropTypes.instanceOf(Function).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  clearList: PropTypes.instanceOf(Function).isRequired,
  clearItem: PropTypes.instanceOf(Function).isRequired,
};

export default enhance(Applications);
